/** Native overflow scrolling with repeated cards; browser owns touch and momentum. */
class FragranceCategoryLoop extends HTMLElement {
  connectedCallback() {
    if (this.hasAttribute('data-editor')) return;
    this.row = this.querySelector(this.getAttribute('data-scroll-row') || '.fragrance-group--category-grid > .group-block-content');
    if (!this.row) return;
    this.originals = [...this.row.children];
    this.observer = new ResizeObserver(() => this.setup());
    this.observer.observe(this.row);
    this.row.addEventListener('scroll', this.onScroll, { passive: true });
    this.row.addEventListener('scrollend', this.onScrollEnd);
    this.row.addEventListener('touchstart', this.onTouchStart, { passive: true });
    this.row.addEventListener('touchend', this.onTouchEnd, { passive: true });
    this.row.addEventListener('touchcancel', this.onTouchEnd, { passive: true });
    if (this.hasAttribute('data-autoplay')) this.frame = requestAnimationFrame(this.advance);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    cancelAnimationFrame(this.frame);
    this.lastFrame = 0;
    clearTimeout(this.settleTimer);
    this.row?.removeEventListener('scroll', this.onScroll);
    this.row?.removeEventListener('scrollend', this.onScrollEnd);
    this.row?.removeEventListener('touchstart', this.onTouchStart);
    this.row?.removeEventListener('touchend', this.onTouchEnd);
    this.row?.removeEventListener('touchcancel', this.onTouchEnd);
    this.width = undefined;
    this.clear();
  }

  clear() {
    clearTimeout(this.settleTimer);
    this.row?.querySelectorAll(':scope > [data-loop-copy]').forEach((copy) => copy.remove());
    this.period = 0;
    if (this.row) this.row.style.removeProperty('scroll-snap-type');
  }

  setup() {
    const width = this.row.clientWidth;
    if (width === this.width) return;
    this.width = width;
    this.clear();
    if (getComputedStyle(this.row).overflowX !== 'auto' || this.row.scrollWidth <= width) return;

    const copySet = () => this.originals.map((item) => {
      const copy = item.cloneNode(true);
      copy.setAttribute('data-loop-copy', '');
      copy.setAttribute('aria-hidden', 'true');
      // Copies remain clickable, but keyboard and assistive technology use the original cards.
      for (const element of [copy, ...copy.querySelectorAll('*')]) {
        element.removeAttribute('id');
        element.removeAttribute('data-shopify-editor-block');
        if (element.matches('img')) element.loading = 'eager';
        if (element.matches('a, button, input, select, textarea, [tabindex]')) element.tabIndex = -1;
      }
      return copy;
    });
    this.copySet = copySet;
    // Give native touch momentum room to finish without resetting scrollLeft mid-swipe.
    const bufferSets = this.hasAttribute('data-autoplay') ? 1 : 3;
    for (let i = 0; i < bufferSets; i++) {
      this.row.prepend(...copySet());
      this.row.append(...copySet());
    }
    this.origin = this.originals[0].getBoundingClientRect().left - this.row.firstElementChild.getBoundingClientRect().left;
    this.period = this.origin / bufferSets;
    // Native snapping can expose a boundary while the identical copies are repositioned.
    this.row.style.scrollSnapType = 'none';
    this.row.scrollLeft = this.origin;
  }

  onTouchStart = () => {
    // Never reset scrollLeft during a gesture or its native momentum.
    this.touching = true;
    clearTimeout(this.settleTimer);
  };

  onTouchEnd = () => {
    this.touching = false;
    this.onScroll();
  };

  onScroll = () => {
    if (this.hasAttribute('data-autoplay')) return;
    this.extendAhead();
    clearTimeout(this.settleTimer);
    if ('onscrollend' in this.row) return;
    // Older browsers lack scrollend. Require a quiet period after touch release.
    this.settleTimer = setTimeout(() => {
      if (!this.touching) this.wrap();
    }, 250);
  };

  extendAhead() {
    if (!this.period || !this.copySet) return;
    const remaining = this.row.scrollWidth - this.row.clientWidth - this.row.scrollLeft;
    if (remaining >= this.period * 2) return;
    // Rapid consecutive flicks may never reach scrollend. Extend the right edge
    // without resetting scrollLeft, moving existing cards, or cancelling momentum.
    for (let i = 0; i < 3; i++) {
      const copies = this.copySet();
      copies.forEach((copy) => copy.setAttribute('data-loop-extension', ''));
      this.row.append(...copies);
    }
  }

  onScrollEnd = () => {
    if (!this.touching && !this.hasAttribute('data-autoplay')) {
      clearTimeout(this.settleTimer);
      this.wrap();
    }
  };

  hasKeyboardFocus() {
    const active = document.activeElement;
    return active !== this.row && this.row.contains(active) && active.matches(':focus-visible');
  }

  wrap = (fromTouch = false) => {
    if (!this.period) return;
    // Mobile browsers can focus the scroll container itself after a swipe.
    // That must not disable recycling; only protect an actual keyboard-focused card.
    if (!fromTouch && this.hasKeyboardFocus()) return;
    const position = this.row.scrollLeft;
    const cycles = Math.floor((position - this.origin + this.period / 2) / this.period);
    if (cycles) this.row.scrollTo({ left: position - cycles * this.period, behavior: 'instant' });
    if (!this.hasAttribute('data-autoplay')) {
      this.row.querySelectorAll(':scope > [data-loop-extension]').forEach((copy) => copy.remove());
    }
  };

  advance = (time) => {
    const elapsed = this.lastFrame ? Math.min(time - this.lastFrame, 50) : 0;
    this.lastFrame = time;
    if (this.period && !matchMedia('(prefers-reduced-motion: reduce)').matches && !this.hasKeyboardFocus()) {
      // Accumulate subpixels so slow movement remains even on browsers that round scrollLeft.
      this.remainder = (this.remainder || 0) + elapsed * 0.03;
      const distance = Math.floor(this.remainder);
      this.remainder -= distance;
      this.row.scrollLeft += distance;
      this.wrap();
    }
    this.frame = requestAnimationFrame(this.advance);
  };
}

if (!customElements.get('fragrance-category-loop')) {
  customElements.define('fragrance-category-loop', FragranceCategoryLoop);
}

if (!customElements.get('fragrance-assurance-loop')) {
  customElements.define('fragrance-assurance-loop', class extends FragranceCategoryLoop {});
}
